import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, AlertTriangle, RefreshCw, Shield, Trash2, 
  CheckSquare, Square, Phone, Loader2, X, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const RESET_CATEGORIES = [
  { key: 'settings', label: 'Settings', description: 'Business settings, bill settings', manual: true, danger: true },
  { key: 'customers', label: 'Customers', description: 'All customer records', manual: true, danger: true },
  { key: 'suppliers', label: 'Suppliers', description: 'All supplier records', manual: true, danger: true },
  { key: 'products', label: 'Products', description: 'All product records', manual: true, danger: true },
  { key: 'stocks', label: 'Stocks', description: 'Stock levels and movements', manual: true, danger: true },
  { key: 'sales', label: 'Sales', description: 'Bills, estimations', manual: false, danger: false },
  { key: 'customer_ledger', label: 'Customer Ledger', description: 'Customer transactions, balances', manual: false, danger: false },
  { key: 'supplier_ledger', label: 'Supplier Ledger', description: 'Supplier transactions, payables', manual: false, danger: false },
  { key: 'grn', label: 'GRN', description: 'Goods received notes', manual: false, danger: false },
  { key: 'expenses', label: 'Expenses', description: 'Expense records', manual: false, danger: false },
  { key: 'payment_collections', label: 'Payment Collections', description: 'Collection records, approvals', manual: false, danger: false },
  { key: 'cash_register_logs', label: 'Cash Register Logs', description: 'Register open/close history', manual: false, danger: false },
];

const ResetBusinessSettings = () => {
  const navigate = useNavigate();
  
  // Category selection - manual categories default to false, others default to true
  const [categories, setCategories] = useState(
    RESET_CATEGORIES.reduce((acc, cat) => {
      acc[cat.key] = !cat.manual;
      return acc;
    }, {})
  );
  
  const [step, setStep] = useState(1); // 1=select, 2=preview, 3=otp, 4=success
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [maskedMobile, setMaskedMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [resetResult, setResetResult] = useState(null);

  const toggleCategory = (key) => {
    setCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAll = () => {
    setCategories(RESET_CATEGORIES.reduce((acc, cat) => {
      acc[cat.key] = true;
      return acc;
    }, {}));
  };

  const deselectAll = () => {
    setCategories(RESET_CATEGORIES.reduce((acc, cat) => {
      acc[cat.key] = false;
      return acc;
    }, {}));
  };

  const selectedCount = Object.values(categories).filter(Boolean).length;

  const handlePreview = async () => {
    if (selectedCount === 0) {
      toast.error('Please select at least one category to reset');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/reset/preview`, {
        categories
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreview(response.data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview reset');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/reset/request-otp`, {
        categories
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMaskedMobile(response.data.mobile);
      setStep(3);
      toast.success('OTP sent to your registered mobile');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/reset/confirm`, {
        categories,
        otp
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResetResult(response.data);
      setStep(4);
      toast.success('Business data reset completed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-business-page" className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => step === 1 ? navigate('/gposp/settings/business') : setStep(step - 1)}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reset Business Data</h1>
          <p className="text-slate-500">Clear selected data to start fresh</p>
        </div>
      </div>

      {/* Step 1: Select Categories */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Warning Banner */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-500 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-red-700">Warning: Data cannot be recovered!</p>
                <p className="text-sm text-red-600 mt-1">
                  Selected data will be permanently deleted. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {/* Category Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Select data to reset:</h3>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-sm text-violet-600 hover:underline"
                >
                  Select All
                </button>
                <span className="text-slate-300">|</span>
                <button
                  onClick={deselectAll}
                  className="text-sm text-violet-600 hover:underline"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {RESET_CATEGORIES.map(cat => (
                <div
                  key={cat.key}
                  onClick={() => toggleCategory(cat.key)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    categories[cat.key]
                      ? cat.danger ? 'bg-red-50 border-red-200' : 'bg-violet-50 border-violet-200'
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {categories[cat.key] ? (
                      <CheckSquare className={cat.danger ? 'text-red-500' : 'text-violet-600'} size={20} />
                    ) : (
                      <Square className="text-slate-400" size={20} />
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${categories[cat.key] ? (cat.danger ? 'text-red-700' : 'text-violet-700') : 'text-slate-800'}`}>
                        {cat.label}
                        {cat.manual && (
                          <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Manual Select</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-500">{cat.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={handlePreview}
            disabled={loading || selectedCount === 0}
            className="w-full py-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCw size={20} />}
            Preview Reset ({selectedCount} selected)
          </button>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className="space-y-6">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-500 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-amber-700">Review before proceeding</p>
                <p className="text-sm text-amber-600 mt-1">
                  The following data will be permanently deleted:
                </p>
              </div>
            </div>
          </div>

          {/* Data Counts */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-800 mb-4">Data to be deleted:</h3>
            <div className="space-y-2">
              {Object.entries(preview.counts).map(([key, count]) => (
                <div key={key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <span className="text-slate-700 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="font-semibold text-red-600">{count} records</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-slate-800">Total Records</span>
              <span className="text-xl font-bold text-red-600">{preview.total_records}</span>
            </div>
          </div>

          <button
            onClick={handleRequestOtp}
            disabled={loading}
            className="w-full py-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Shield size={20} />}
            Request OTP to Confirm Reset
          </button>
        </div>
      )}

      {/* Step 3: OTP Verification */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-500 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-red-700">FINAL WARNING!</p>
                <p className="text-sm text-red-600 mt-1">
                  This action cannot be undone. All selected data will be permanently deleted.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <Phone className="mx-auto text-violet-600 mb-4" size={48} />
            <p className="text-slate-700 mb-2">OTP sent to:</p>
            <p className="text-2xl font-mono font-bold text-slate-800 mb-6">{maskedMobile}</p>

            <div className="max-w-xs mx-auto">
              <label className="block text-sm text-slate-600 mb-2 text-left">Enter 6-digit OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="______"
                className="w-full text-center text-2xl font-mono tracking-[0.5em] px-4 py-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500"
                maxLength={6}
              />
            </div>
          </div>

          <button
            onClick={handleConfirmReset}
            disabled={loading || otp.length !== 6}
            className="w-full py-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={20} />}
            CONFIRM RESET
          </button>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && resetResult && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-green-700 mb-2">Reset Completed!</h3>
            <p className="text-green-600">Your selected business data has been cleared.</p>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-800 mb-4">Reset Summary:</h3>
            <div className="space-y-2">
              {Object.entries(resetResult.counts).map(([key, count]) => (
                <div key={key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <span className="text-slate-700 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="font-semibold text-slate-600">{count} deleted</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <span className="font-semibold text-slate-800">Total Deleted</span>
              <span className="text-xl font-bold text-slate-800">{resetResult.total_deleted}</span>
            </div>
          </div>

          <button
            onClick={() => navigate('/gposp/dashboard')}
            className="w-full py-4 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 flex items-center justify-center gap-2"
          >
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

export default ResetBusinessSettings;
